// src/pages/Admin/correspondencia/NotificarLlegadaModal.tsx
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
    X, Bell, Camera, Upload, Trash2, CheckCircle2, 
    MessageSquare, Send, RefreshCw, AlertCircle, Eye, Shield, User, FileText, Check, Copy
} from 'lucide-react';
import { Correspondencia, notificarLlegadaCorrespondencia } from '../../../services/correspondencia';
import { convertImageToWebP } from '../../../utils/imageCompressor';


interface NotificarLlegadaModalProps {
    isOpen: boolean;
    onClose: () => void;
    correspondencia: Correspondencia;
    onSuccess: () => void;
}

const NotificarLlegadaModal: React.FC<NotificarLlegadaModalProps> = ({
    isOpen,
    onClose,
    correspondencia,
    onSuccess
}) => {
    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [imagenPreview, setImagenPreview] = useState<string | null>(correspondencia.imagen || null);
    const [observaciones, setObservaciones] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notificadoExitoso, setNotificadoExitoso] = useState<Correspondencia | null>(null);

    // Cámara en vivo
    const [modoCamaraEnVivo, setModoCamaraEnVivo] = useState(false);
    const [camaraError, setCamaraError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const stopCameraStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const handleClose = () => {
        stopCameraStream();
        setModoCamaraEnVivo(false);
        setCamaraError(null);
        setImagenFile(null);
        setImagenPreview(null);
        setObservaciones('');
        setError(null);
        setNotificadoExitoso(null);
        onClose();
    };

    const startLiveCamera = async () => {
        setCamaraError(null);
        setModoCamaraEnVivo(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (err: any) {
            console.error('Error accediendo a la cámara:', err);
            setModoCamaraEnVivo(false);
            setCamaraError('No se pudo acceder a la cámara. Revisa los permisos o adjunta un archivo.');
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `recepcion_${correspondencia.id}_${Date.now()}.webp`, { type: 'image/webp' });
                    setImagenFile(file);
                    setImagenPreview(canvas.toDataURL('image/webp', 0.88));
                    stopCameraStream();
                    setModoCamaraEnVivo(false);
                    setCamaraError(null);
                }
            }, 'image/webp', 0.88);
        }
    };

    const cancelLiveCamera = () => {
        stopCameraStream();
        setModoCamaraEnVivo(false);
        setCamaraError(null);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const webpFile = await convertImageToWebP(file);
            setImagenFile(webpFile);
            const reader = new FileReader();
            reader.onloadend = () => setImagenPreview(reader.result as string);
            reader.readAsDataURL(webpFile);
            setCamaraError(null);
        }
    };

    const handleRemovePhoto = () => {
        setImagenFile(null);
        setImagenPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);
        try {
            const updated = await notificarLlegadaCorrespondencia(correspondencia.id, {
                imagen: imagenFile,
                observaciones: observaciones.trim() || undefined
            });
            setNotificadoExitoso(updated);
            onSuccess();
        } catch (err: any) {
            console.error('Error notificando llegada:', err);
            const msg = err?.response?.data?.detail || 'Ocurrió un error al notificar la llegada del paquete.';
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getTrackingUrl = (item: Correspondencia) => {
        const param = item.token_verificacion || item.codigo || String(item.id);
        return `${window.location.origin}/seguimiento/${encodeURIComponent(param)}`;
    };

    const generarLinkWhatsApp = (item: Correspondencia) => {
        const telefono = item.destinatario_telefono || item.remitente_telefono || '';
        const digits = telefono.replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;
        const nombre = item.destinatario_nombre || item.cliente_nombre || 'Estimado(a)';
        const trackingUrl = getTrackingUrl(item);

        const mensaje = `Hola *${nombre}*, te informamos que tu paquete *${item.codigo || `#${item.id}`}* ya ha llegado a recepción de *Comarca Coworking* y se encuentra listo para ser retirado.\n\n` +
            `🔐 *PIN Secreto de Retiro:* ${item.pin_seguridad}\n\n` +
            `📱 *Ver proceso y comprobante QR de retiro:*\n${trackingUrl}\n\n` +
            `_Muestra tu QR o dicta tu PIN en recepción para entregártelo._`;

        return `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`;
    };

    if (!isOpen) return null;

    return createPortal(
        <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-3 sm:p-4 md:p-6 backdrop-blur-md font-['DM_Sans',sans-serif] overflow-y-auto"
            onClick={handleClose}
        >
            <div 
                className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden animate-scale-up my-auto border border-gray-100"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-100 rounded-t-3xl flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-gradient-to-tr from-amber-500 to-orange-400 text-white rounded-2xl shadow-sm">
                            <Bell className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">
                                {notificadoExitoso ? '¡Llegada Notificada con Éxito!' : 'Notificar Llegada de Paquete'}
                            </h3>
                            <p className="text-xs text-gray-500">
                                {correspondencia.codigo || `#${correspondencia.id}`} • Para: {correspondencia.destinatario_nombre || 'Cliente'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {notificadoExitoso ? (
                    <div className="p-6 space-y-5 text-center overflow-y-auto flex-1 custom-scroll">
                        <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>

                        <div>
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-mono font-bold text-xs rounded-full uppercase tracking-wider">
                                Estado: Listo para Retiro (Notificado)
                            </span>
                            <h4 className="text-xl font-black text-gray-900 mt-2">
                                Paquete Recibido Físicamente
                            </h4>
                            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                                Se ha confirmado la custodia del paquete. El cliente y destinatario ya pueden ver la actualización en su proceso de seguimiento.
                            </p>
                        </div>

                        {/* Botón WhatsApp */}
                        <div className="space-y-3 max-w-sm mx-auto pt-2">
                            <a
                                href={generarLinkWhatsApp(notificadoExitoso)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3.5 px-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-xs rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-2.5">
                                    <MessageSquare className="w-5 h-5 fill-white" />
                                    <div className="text-left">
                                        <div className="text-xs font-bold">Enviar Notificación por WhatsApp</div>
                                        <div className="text-[10px] font-normal text-white/90">
                                            {notificadoExitoso.destinatario_nombre} ({notificadoExitoso.destinatario_telefono || 'Sin teléfono'})
                                        </div>
                                    </div>
                                </div>
                                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>

                            <button
                                onClick={handleClose}
                                className="w-full py-2.5 px-4 bg-gray-800 hover:bg-black text-white font-bold text-xs rounded-xl transition-all"
                            >
                                Listo, Cerrar
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1 custom-scroll">
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Resumen del paquete y personas involucradas */}
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-2xl space-y-2.5 text-xs text-gray-700">
                            <div className="flex items-center justify-between border-b pb-2">
                                <span className="font-mono font-bold text-[#61257b]">
                                    {correspondencia.codigo || `#${correspondencia.id}`}
                                </span>
                                <span className="font-mono font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                    PIN: {correspondencia.pin_seguridad}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <span className="text-[10px] text-gray-400 block">Dejado por (Remitente):</span>
                                    <p className="font-semibold text-gray-800">
                                        {correspondencia.remitente_nombre} {correspondencia.remitente_apellido || ''}
                                    </p>
                                    {correspondencia.remitente_telefono && (
                                        <span className="text-[10px] text-gray-500">Tel: {correspondencia.remitente_telefono}</span>
                                    )}
                                </div>
                                <div>
                                    <span className="text-[10px] text-gray-400 block">A retirar por (Destinatario):</span>
                                    <p className="font-semibold text-[#61257b]">
                                        {correspondencia.destinatario_nombre || 'Cliente'}
                                    </p>
                                    {correspondencia.destinatario_telefono && (
                                        <span className="text-[10px] text-gray-500">Tel: {correspondencia.destinatario_telefono}</span>
                                    )}
                                </div>
                            </div>
                            {correspondencia.descripcion && (
                                <div className="pt-1.5 border-t border-gray-200/60">
                                    <span className="text-[10px] text-gray-400 block">Descripción declarada:</span>
                                    <p className="text-gray-600 italic">{correspondencia.descripcion}</p>
                                </div>
                            )}
                        </div>

                        {/* FOTOGRAFÍA OPCIONAL DE EVIDENCIA EN RECEPCIÓN */}
                        <div className="space-y-3 pt-1 border-t border-gray-100">
                            <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <Camera className="w-4 h-4 text-[#61257b]" />
                                    Foto de Evidencia en Recepción (Opcional)
                                </span>
                                <span className="text-[10px] font-normal text-gray-400">Opcional</span>
                            </label>

                            {/* Cámara en vivo */}
                            {modoCamaraEnVivo ? (
                                <div className="relative rounded-2xl overflow-hidden bg-black aspect-video flex flex-col items-center justify-center border-2 border-[#61257b]">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute bottom-3 flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={capturePhoto}
                                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl shadow-lg flex items-center gap-1.5 transition-all"
                                        >
                                            <Camera className="w-4 h-4" />
                                            Capturar Fotografía
                                        </button>
                                        <button
                                            type="button"
                                            onClick={cancelLiveCamera}
                                            className="px-3 py-2 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-xl backdrop-blur-sm transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            ) : imagenPreview ? (
                                <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 max-w-xs mx-auto">
                                    <img src={imagenPreview} alt="Evidencia de recepción" className="w-full h-44 object-contain bg-white" />
                                    <button
                                        type="button"
                                        onClick={handleRemovePhoto}
                                        className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow transition-colors"
                                        title="Eliminar foto"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={startLiveCamera}
                                        className="p-4 border-2 border-dashed border-blue-200 hover:border-[#61257b] bg-blue-50/40 hover:bg-blue-50 text-[#61257b] rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all group"
                                    >
                                        <Camera className="w-6 h-6 group-hover:scale-110 transition-transform" />
                                        <span className="text-xs font-bold">Activar Cámara</span>
                                        <span className="text-[10px] text-gray-500">Tomar foto ahora</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="p-4 border-2 border-dashed border-gray-200 hover:border-gray-400 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all group"
                                    >
                                        <Upload className="w-6 h-6 group-hover:scale-110 transition-transform text-gray-500" />
                                        <span className="text-xs font-bold">Subir Imagen</span>
                                        <span className="text-[10px] text-gray-500">JPG, PNG o WEBP</span>
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                </div>
                            )}

                            {camaraError && (
                                <p className="text-[11px] text-red-600">{camaraError}</p>
                            )}
                        </div>

                        {/* Observaciones opcionales */}
                        <div className="space-y-1.5 pt-1 border-t border-gray-100">
                            <label className="block text-xs font-medium text-gray-700 flex items-center gap-1">
                                <FileText className="w-3.5 h-3.5 text-gray-400" />
                                Observaciones de Recepción (Opcional)
                            </label>
                            <input
                                type="text"
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                                placeholder="Ej: Dejado por moto delivery en sobre cerrado a las 12:30..."
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none"
                            />
                        </div>

                        {/* Botones */}
                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold rounded-xl hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Notificando...
                                    </>
                                ) : (
                                    <>
                                        <Bell className="w-4 h-4" />
                                        Confirmar y Notificar
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>,
        document.body
    );
};

export default NotificarLlegadaModal;
